import React, { useEffect, useState } from "react";
import {
  Button,
  Iconography,
  IconographySizes,
  Notification,
} from "@hid-galaxy-ui/galaxy-react";
import { useNavigate, useParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../../redux";
import { Tab, Tabs, Box } from "@mui/material";
import Overview from "./overview/index";
import Calculator from "./calculator/index";
import PerformanceTrend from "./performance-trend/index";
import BenefitsLog from "./benefits-log/index";
import { getPartnerLicenceListSagaAction } from "../../../../redux/saga/partner-program/licence-management/saga-actions";
import { getPartnerTierListSagaAction } from "../../../../redux/saga/partner-program/saga-actions";
import "./PartnerProgram.scss";
import { getPartnerEngagementCategoryListSagaAction } from "../../../../redux/saga/partner-program/engagement-configuration/saga-actions";
import { getPartnerRegionalMultiplierListSagaAction } from "../../../../redux/saga/partner-program/regional-multipliers/saga-actions";
import PartnerShortDetails from "../components/PartnerShortDetails";
import { usePartnerSessionModuleReadOnly } from "../../../../hooks/usePartnerSessionPermissions";
import { useIsPartnerProgramEligible } from "../../../../hooks/useIsPartnerProgramEligible";
import { PARTNER_PORTAL_PERMISSION_KEYS as PP_KEYS } from "../../../../utils/permissions/partnerPortalPermissionKeys";

const BENEFITS_LOG_TAB_INDEX = 3;

const TabPanel: React.FC<{
  index: number;
  value: number;
  children: React.ReactNode;
}> = ({ index, value, children }) => {
  return (
    <Box
      sx={{
        mt: 2,
        display: value === index ? "block" : "none",
      }}
    >
      {children}
    </Box>
  );
};

const PartnerProgramPartnerView = () => {
  const [ppTab, setPpTab] = useState<number>(0);
  const [toasts, setToasts] = useState<any[]>([]);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { partnerID } = useParams();
  const { partnerDetails } = useAppSelector((state) => state.partner_details);
  const isPartnerProgramEligible = useIsPartnerProgramEligible();
  const programReadOnly = usePartnerSessionModuleReadOnly(
    PP_KEYS.PARTNER_PROGRAM_MANAGEMENT
  );

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  useEffect(() => {
    if (!isPartnerProgramEligible && partnerDetails) {
      navigate(`/partner/${partnerID}/dashboard/home`, { replace: true });
    }
  }, [isPartnerProgramEligible, partnerDetails, navigate, partnerID]);

  useEffect(() => {
    if (!isPartnerProgramEligible) {
      return;
    }
    dispatch(getPartnerTierListSagaAction(1));
    dispatch(getPartnerLicenceListSagaAction(1));
    dispatch(getPartnerEngagementCategoryListSagaAction());
    if (partnerDetails?.ID) {
      dispatch(getPartnerRegionalMultiplierListSagaAction(partnerDetails.ID));
    }
  }, [dispatch, partnerDetails?.ID, isPartnerProgramEligible]);

  if (!isPartnerProgramEligible) {
    return null;
  }

  return (
    <div className="partner-program-partner-view">
      <div className="partner-program-container">
        <PartnerShortDetails />
        <Notification toasts={toasts} setToasts={setToasts} />
        <div className="partner-program-header">
          <div className="header-left">
            <h1 className="header-title">Partner Program</h1>
            <p className="header-subtitle">
              View your yearly tier progress, points, and engagement.
          </p>
          </div>
          {ppTab === BENEFITS_LOG_TAB_INDEX && (
            <div className="header-right">
              <Button
                variant="primary"
                className="benefits-comparison-btn"
                normalCase={true}
                label={
                  <span className="benefits-comparison-btn__label">
                    Benefits Comparison
                    <Iconography icon="eye" size={IconographySizes.Small} />
                  </span>
                }
                onClick={() =>
                  window.open(
                    `/partner/${partnerID}/dashboard/partner-program/benefits-comparison`,
                    "_blank",
                    "noopener,noreferrer"
                  )
                }
              />
            </div>
          )}
        </div>
      <Tabs
        value={ppTab}
        onChange={(_, v) => setPpTab(v)}
        aria-label="Partner Program Tabs"
        className="pp-config-tabs"
        variant="scrollable"
        scrollButtons="auto"
      >
        <Tab label="Performance Trend" />
        <Tab label="Overview" />
        <Tab label="Calculator (Running Year)" />
        <Tab label="Benefits Log" />
      </Tabs>
        <TabPanel index={0} value={ppTab}>
          <PerformanceTrend readOnly={programReadOnly} />
        </TabPanel>
        <TabPanel index={1} value={ppTab}>
          <Overview readOnly={programReadOnly} />
        </TabPanel>
        <TabPanel index={2} value={ppTab}>
          <Calculator readOnly={programReadOnly} />
        </TabPanel>
        <TabPanel index={BENEFITS_LOG_TAB_INDEX} value={ppTab}>
          <BenefitsLog readOnly={programReadOnly} />
        </TabPanel>
      </div>
    </div>
  );
};

export default PartnerProgramPartnerView;
